import { Box } from '@mui/material';
import { useEffect, useRef, useState, useCallback } from 'react';
import { HIERARCHY_BORDER_STYLE } from '../styles';
import useDebounce from '../../../../utils/useDebounce';

const useHierarchyConnectorVertical = () => {
    const mainContainerRef = useRef(null);
    const expandIconRef = useRef(null);
    const lastChildRef = useRef(null);
    const hierarchyConnectorVerticalRef = useRef(null);
    const [connectorPosition, setConnectorPosition] = useState({ top: 0, left: 0, height: 0 });

    // Calculate connector position based on expandIconRef relative to container
    const updatePosition = useCallback(() => {
        if (!expandIconRef?.current || !mainContainerRef?.current) return;

        const expandIconRect = expandIconRef.current.getBoundingClientRect();
        const mainContainerRect = mainContainerRef.current.getBoundingClientRect();
        const lastChildRect = lastChildRef?.current?.getBoundingClientRect();

        const top = expandIconRect.bottom - mainContainerRect.y - 1;
        const left = expandIconRect.left + expandIconRect.width / 2 - mainContainerRect.left - 1;
        const height = lastChildRect
            ? lastChildRect.bottom - lastChildRect.height / 2 - expandIconRect.bottom
            : 0;

        setConnectorPosition({ top, left, height });
    }, []);

    // Debounced version of updatePosition for resize events
    const debouncedUpdatePosition = useDebounce(updatePosition, 10);

    useEffect(() => {
        debouncedUpdatePosition();

        // Use ResizeObserver to monitor container and child size changes
        const resizeObserver = new ResizeObserver(() => {
            debouncedUpdatePosition();
        });

        if (mainContainerRef.current) resizeObserver.observe(mainContainerRef.current);
        if (expandIconRef.current) resizeObserver.observe(expandIconRef.current);
        if (lastChildRef.current) resizeObserver.observe(lastChildRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [debouncedUpdatePosition]);

    const renderConnector = (isVisible) => (
        <Box
            aria-hidden
            ref={hierarchyConnectorVerticalRef}
            sx={{
                position: 'absolute',
                transition: 'height 0.2s ease-in-out',
                height: isVisible ? connectorPosition.height : 0,
                top: connectorPosition.top,
                left: connectorPosition.left,
                width: 0,
                borderLeft: HIERARCHY_BORDER_STYLE,
            }}
        />
    );

    return {
        lastChildRef,
        expandIconRef,
        hierarchyConnectorVerticalRef,
        mainContainerRef,
        renderConnector,
    };
};

export default useHierarchyConnectorVertical;
